// The dispatch desk's read side. Mirrors src/server/routes/agentDesk.ts exactly; every
// MUTATION still goes through `agentApi` (src/client/api/agent.ts), which owns the runner.
import { request } from './client.js';
import type { Degradable } from '../../shared/problems.js';
import type { FaultClass } from '../../shared/agentFaults.js';

export type DispatchKind = 'new_evidence' | 'deliverables' | 'acceptance_criteria' | 'test_cases';

export interface DispatchCard {
  kind: DispatchKind;
  label: string;
  covers: number;
  unit: string;
  unit_plural: string;
  subject: string[];
  subject_more: number;
  blurb: string;
  ready: boolean;
  blocked_reason: string | null;
  document_ids: number[];
  since_run: number | null;
}

export interface DispatchPlan extends Degradable {
  engagement_id: number;
  in_scope: number;
  still_draft: number;
  cards: DispatchCard[];
}

export type SealTone = 'ok' | 'attend' | 'broken' | 'none';

export interface LedgerRun {
  id: number;
  mode: string;
  status: string;
  pass_label: string;
  discipline: string | null;
  closed: boolean;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  scope_n: number;
  docs_read: number;
  docs_cited: number;
  claimed: number;
  filed: number;
  held: number;
  rejected: number;
  pending: number;
  facts: number;
  events: number;
  turns: number;
  tool_calls: number;
  tokens_in: number;
  tokens_out: number;
  lookup_misses: number;
  has_account: boolean;
  error: string | null;
  seal: { tone: SealTone; label: string } | null;
  flag: { tone: 'attend' | 'broken'; text: string } | null;
  faults: FaultClass[];
  live: boolean;
}

export interface Standing {
  pushbacks_total: number;
  top_fault: { text: string; n: number } | null;
  review_rejections: number;
  top_reject: { reason: string; n: number } | null;
  runs_total: number;
  runs_closed: number;
  runs_unclosed: number;
}

export interface Ledger extends Degradable {
  engagement_id: number;
  runs: LedgerRun[];
  standing: Standing;
}

export interface DeskProposal {
  id: number;
  kind: string;
  status: string;
  payload: Record<string, unknown>;
  evidence: Array<{ document_id?: number; document_ref?: string; quoted_span?: string }> | null;
  reject_reason: string | null;
  reviewer_note: string | null;
  created_at: string;
}

export interface DeskTranscriptPart {
  type: string;
  text?: string;
  tool?: string;
  status?: string | null;
  title?: string | null;
  input?: unknown;
  output?: string | null;
  error?: string | null;
}

export interface DeskTranscriptMessage {
  id: string | null;
  role: string;
  created: number | null;
  model: string | null;
  parts: DeskTranscriptPart[];
}

export interface RunAudit extends Degradable {
  id: number;
  engagement_id: number;
  engagement_code: string;
  mode: string;
  status: string;
  pass_label: string;
  prompt: string | null;
  discipline: string | null;
  closed: boolean;
  closing_account: string | null;
  transcript: DeskTranscriptMessage[];
  proposals: DeskProposal[];
  documents_in_scope: Array<{ id: number; ref: string; original_filename: string; effective_date: string | null }>;
  docs_covered: number[];
  provenance: {
    model_id: string | null;
    workspace_commit: string | null;
    skill_commit: string | null;
    config_hash: string | null;
    session_id: string | null;
    turns: number;
    tool_calls: number;
    tokens_in: number;
    tokens_out: number;
    lookup_misses: number;
    rejections: number;
  };
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  error: string | null;
}

export interface DeskProgress extends Degradable {
  run_id: number;
  status: string;
  started_at: string | null;
  discipline: string | null;
  turns: number;
  tool_calls: number;
  totals: { proposals: number; questions: number };
  docs: Array<{ ref: string; proposals: number; questions: number }>;
  recent: Array<{ kind: string; text: string; at: string; document_ref: string | null }>;
}

export const agentDeskApi = {
  dispatch: (engagementId: number) => request<DispatchPlan>(`/agent-desk/dispatch?engagement_id=${engagementId}`),
  ledger: (engagementId: number, limit = 50) =>
    request<Ledger>(`/agent-desk/ledger?engagement_id=${engagementId}&limit=${limit}`),
  run: (id: number) => request<RunAudit>(`/agent-desk/runs/${id}`),
  progress: (id: number) => request<DeskProgress>(`/agent-desk/runs/${id}/progress`),
};

export default agentDeskApi;
